import React from "react";
import { Quote } from "lucide-react";

/**
 * Elegant quoted-post block (Threads/X-style).
 * - Left navy border, cream/gray bg, italic body, small author line + timestamp.
 * - Clickable → smooth-scrolls to the original in the DOM (via #reply-<id> hash).
 * - If the quote target is missing/deleted, renders a "Postingan dihapus" placeholder.
 *
 * Props:
 *   snapshot: { type: "reply"|"thread", reply_id?, thread_id?, title?, user_name, created_at, excerpt }
 *   testid?: string
 *   compact?: boolean — used inside the composer preview (smaller padding, no border-radius)
 */
export default function QuoteBlock({ snapshot, testid, compact = false }) {
  if (!snapshot) return null;
  const isThread = snapshot.type === "thread";
  const anchorId = isThread ? null : `reply-${snapshot.reply_id}`;
  const goTo = () => {
    if (!anchorId) return;
    const el = document.getElementById(anchorId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[#1E3A8A]/30");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#1E3A8A]/30"), 1500);
    }
  };
  const dateStr = snapshot.created_at
    ? new Date(snapshot.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })
    : "";

  return (
    <div
      onClick={anchorId ? goTo : undefined}
      data-testid={testid || "quote-block"}
      role={anchorId ? "button" : undefined}
      tabIndex={anchorId ? 0 : undefined}
      onKeyDown={(e) => {
        if (anchorId && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); goTo(); }
      }}
      className={
        "border-l-4 border-[#1E3A8A] bg-[#F9FAFB] " +
        (compact ? "p-3 rounded-r-lg" : "p-4 rounded-r-xl mt-1 mb-3 ") +
        (anchorId ? " cursor-pointer hover:bg-[#F3F4F6] transition-colors" : "")
      }
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#1E3A8A] font-semibold mb-1.5">
        <Quote size={12} strokeWidth={2} />
        <span>{isThread ? "Mengutip thread awal" : "Mengutip balasan"}</span>
        {snapshot.user_name && <span className="normal-case tracking-normal text-[#4B5563] font-normal">· oleh {snapshot.user_name}</span>}
        {dateStr && <span className="normal-case tracking-normal text-[#9CA3AF] font-normal">· {dateStr}</span>}
      </div>
      {isThread && snapshot.title && (
        <div className="font-serif-display text-sm text-[#1F2937] mb-0.5 line-clamp-1">{snapshot.title}</div>
      )}
      <div className="text-sm text-[#4B5563] italic leading-snug line-clamp-2 whitespace-pre-wrap">
        {snapshot.excerpt || "(Postingan kosong)"}
      </div>
    </div>
  );
}

export function DeletedQuoteBlock({ testid }) {
  return (
    <div data-testid={testid || "quote-deleted"}
      className="border-l-4 border-[#D1D5DB] bg-[#F9FAFB] p-3 rounded-r-xl mt-1 mb-3 italic text-xs text-[#9CA3AF]">
      Postingan yang dikutip telah dihapus.
    </div>
  );
}
